import { collection, doc, serverTimestamp } from 'firebase/firestore'
import { onSnapshot, updateDoc } from '@/lib/metered'
import { db } from '@/lib/firebase'
import { recordChange } from '@/lib/activity'
import { useSharedCollection } from '@/lib/sharedCollection'
import type { KeepDecision, Product } from './types'
import { productChanges, type ProductEdit } from './changes'

/** `enabled` mirrors `useDecisions` — see the note there. The global search needs it. */
export function useProducts(enabled = true) {
  const { rows, loading, error } = useSharedCollection<Product>(
    'products',
    (onRows, onError) => onSnapshot(
      collection(db, 'products'),
      (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Product)),
      onError,
    ),
    enabled,
  )
  return { products: rows, loading, error }
}

/** Re-exported so callers keep one import for "what a product edit is". */
export type { ProductEdit } from './changes'

export async function saveProduct(
  product: Product,
  edit: ProductEdit,
  actor: { email: string; name?: string | null },
  /** The catalogue, so a bundle's members can be logged by name rather than by id. */
  all: Product[] = [],
) {
  const keep = (edit.keep ?? product.keep) as KeepDecision
  await updateDoc(doc(db, 'products', product.id), {
    ...edit,
    decided: keep !== '',
    filledInBy: actor.email,
    decidedBy: actor.email,
    decidedAt: serverTimestamp(),
  })

  for (const change of productChanges(product, edit, all)) {
    await recordChange(['products', product.id], change, actor)
  }
}
